// Turning a composed post body into the form a given server renders correctly.
// Pure functions — no DOM, no network, no global state.
//
// The composer writes markdown: greentext lines that begin with `>`, and
// `[>>ref](url)` links for every reference (see quotes.js). That renders the
// way an imageboard should on Mitra, whose comrak options enable the greentext
// extension and hard line breaks, so `>foo` stays literal text and a single
// newline is a line break.
//
// Pleroma runs Earmark on `text/markdown` instead — plain CommonMark, with
// neither of those. There a leading `>` opens a blockquote, so greentext turns
// into an indent and a `>>ref` at the start of a line turns into a *nested*
// blockquote, and single newlines aren't breaks at all, so the whole post
// collapses into one paragraph.
//
// Pleroma also accepts `text/html` and stores it close to verbatim (its
// scrubber allows `a[href]`, `br` and `p`), so those servers get HTML and we
// do the rendering ourselves: the body is escaped, which leaves `>` as a
// literal character for the client-side greentext pass to colour, each
// markdown link becomes an anchor, and newlines become explicit breaks.
//
// Mastodon renders neither. It has no `content_type` at all — the field is a
// Pleroma extension — and stores every status as plain text, so HTML would show
// its tags and markdown its punctuation: a reference went out as the literal
// `[>>712611578](https://…)`. Those servers get the body flattened to text,
// where a reference becomes `>>ref` followed by its URL (which Mastodon's own
// linkifier turns into a link). Greentext needs nothing — a leading `>` in
// plain text stays a `>`. That URL is redundant to a chan-fe reader, who has
// the ref itself, so on the way back in it's folded away behind it the way a
// ref's `@mention` is (absorbRefMentions in app.js).

import { safeUrl } from "./media.js";
import { isMitraServer } from "./quotes.js";

// `[label](url)`. The label can't span lines and the URL can't contain spaces
// or parentheses, which covers everything the composer writes and keeps this
// from swallowing prose that merely contains brackets.
const MARKDOWN_LINK_RE = /\[([^\]\n]*)\]\(([^()\s]+)\)/g;

export function escapeHTML(s) {
  if (s == null) return "";
  return String(s).replace(/[&<>"']/g, (c) => ({
    "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;"
  }[c]));
}

// Whether this server takes an HTML post body. Pleroma lists what it accepts
// at `pleroma.metadata.post_formats`; anything that doesn't say so gets left
// alone rather than guessed at.
export function supportsHtmlPosts(instance) {
  const formats = instance?.pleroma?.metadata?.post_formats;
  return Array.isArray(formats) && formats.includes("text/html");
}

// Whether this server renders the composer's markdown at all. Advertised the
// same way — Mitra lists both formats, Pleroma lists whichever it was built
// with. A server that lists nothing gets the plain-text treatment; Mitra is
// checked as well in case a build predates the metadata.
export function supportsMarkdownPosts(instance) {
  const formats = instance?.pleroma?.metadata?.post_formats;
  if (Array.isArray(formats)) return formats.includes("text/markdown");
  return isMitraServer(instance);
}

// The composer's markdown body as plain text, for a server that renders
// neither dialect. `[label](url)` becomes `label url`, so a reference still
// reads as its `>>ref` and its target is still reachable — the URL is a bare
// one, which every server's linkifier picks up. Everything else the composer
// writes is already plain text.
//
// A link target that isn't a safe scheme is left exactly as typed rather than
// promoted to a bare URL of its own.
export function composeBodyToPlainText(text) {
  return String(text ?? "").replace(MARKDOWN_LINK_RE, (match, label, url) => {
    const href = safeUrl(url);
    if (!href) return match;
    const shown = label.trim();
    return shown ? `${shown} ${href}` : href;
  });
}

// The composer's markdown body as HTML. Everything is escaped except the
// anchors built from markdown links, so nothing the user typed can inject
// markup, and a link whose target isn't a safe scheme stays literal text.
export function composeBodyToHtml(text) {
  const src = String(text ?? "");
  let out = "";
  let last = 0;

  MARKDOWN_LINK_RE.lastIndex = 0;
  for (let m; (m = MARKDOWN_LINK_RE.exec(src)) !== null; ) {
    out += escapeHTML(src.slice(last, m.index));
    const href = safeUrl(m[2]);
    out += href
      ? `<a href="${escapeHTML(href)}">${escapeHTML(m[1])}</a>`
      : escapeHTML(m[0]);
    last = m.index + m[0].length;
  }
  out += escapeHTML(src.slice(last));

  // Last, so a newline can never land inside an attribute: the link pattern
  // already refuses to match across one.
  return out.replace(/\r\n|\r|\n/g, "<br>");
}
